// ... imports
import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
    Loader2,
    Send,
    Building2,
    ChevronDown,
    ChevronUp,
    HelpCircle,
    FileText,
    AlertTriangle,
    CheckCircle,
    ArrowRight
} from 'lucide-react'
import * as confettiLib from 'canvas-confetti'
const confetti = (confettiLib as any).default || confettiLib

// Components
import { TrustBadges } from '@/components/contract/TrustBadges'
import { SignatureField } from '@/components/contract/SignatureField'
import { ConsentCheckboxes } from '@/components/contract/ConsentCheckboxes'
import { ProgressTimeline } from '@/components/contract/ProgressTimeline'
import { SaaSContextCard } from '@/components/contract/SaaSContextCard'

// Services
import { contractsApi, ContractPublicInfo } from '@/services/contracts-api'

// Helper to parse currency string to number
const parsePrice = (val: string | number | undefined): number => {
    if (!val) return 0
    if (typeof val === 'number') return val
    const clean = val.replace(/[^\d,.-]/g, '').replace(',', '.')
    const parsed = parseFloat(clean)
    return isNaN(parsed) ? 0 : parsed
}

// ==================== Page Component ====================
export function SaaSContractSignPage() {
    const { uuid } = useParams<{ uuid: string }>()
    // navigate unused

    // State
    const [loading, setLoading] = useState(true)
    const [signing, setSigning] = useState(false)
    const [success, setSuccess] = useState(false) // Success State
    const [error, setError] = useState<string | null>(null)
    const [contract, setContract] = useState<ContractPublicInfo | null>(null)

    // Form State
    const [documentInput, setDocumentInput] = useState('')
    const [signatureData, setSignatureData] = useState<string | null>(null)
    const [signatureValid, setSignatureValid] = useState(false)
    const [allConsentsGiven, setAllConsentsGiven] = useState(false)
    const [currentStep, setCurrentStep] = useState('review') // review, consent, signature, confirmation
    const [showFullContract, setShowFullContract] = useState(false)

    // ==================== Load Contract ====================
    useEffect(() => {
        const loadContract = async () => {
            if (!uuid) return
            try {
                const data = await contractsApi.getContractPublicInfo(uuid)
                setContract(data)
            } catch (err: any) {
                console.error('Error loading contract:', err)
                setError(err.message || 'Erro ao carregar contrato.')
            } finally {
                setLoading(false)
            }
        }
        loadContract()
    }, [uuid])

    // ==================== Step Management ====================
    useEffect(() => {
        if (allConsentsGiven && currentStep === 'review') {
            setCurrentStep('consent')
        }
        if (signatureValid && currentStep === 'consent') {
            setCurrentStep('signature')
        }
    }, [allConsentsGiven, signatureValid, currentStep])

    // ==================== Handlers ====================
    const handleSignatureChange = (data: string | null, isValid: boolean) => {
        setSignatureData(data)
        setSignatureValid(isValid)
    }

    const handleConsentChange = (allConsented: boolean, _consents: Record<string, boolean>) => {
        setAllConsentsGiven(allConsented)
    }

    const handleSign = async () => {
        if (!uuid || !contract) return

        if (!signatureValid) {
            toast.error('Por favor, assine o contrato.')
            return
        }
        if (!allConsentsGiven) {
            toast.error('Aceite os termos para continuar.')
            return
        }
        if (!documentInput || documentInput.length < 5) {
            toast.error('Confirme seu CPF ou CNPJ.')
            return
        }

        setSigning(true)
        setCurrentStep('confirmation')

        try {
            await contractsApi.signContract(uuid, {
                signer_name: contract.client_name || contract.company_name,
                signer_cpf: documentInput,
                signature_data: signatureData!
            })

            // Success Action
            setSuccess(true)
            confetti({
                particleCount: 150,
                spread: 70,
                origin: { y: 0.6 }
            })
            toast.success('Contrato assinado com sucesso!')

        } catch (err: any) {
            console.error('Error signing:', err)
            toast.error(err.message || 'Erro ao assinar.')
            setSigning(false)
            setCurrentStep('signature')
        }
    }

    // ==================== Render Loading / Error ====================
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
                <Loader2 className="w-12 h-12 text-sky-400 animate-spin" />
            </div>
        )
    }

    if (error || !contract) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
                <div className="bg-red-500/10 border border-red-500/30 p-8 rounded-2xl text-center max-w-md">
                    <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-white mb-2">Erro</h2>
                    <p className="text-red-300">{error || 'Contrato não encontrado.'}</p>
                </div>
            </div>
        )
    }

    // ==================== Render Success View ====================
    if (success) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
                <div className="bg-white/5 border border-white/10 p-10 rounded-3xl text-center max-w-lg w-full backdrop-blur-xl shadow-2xl animate-in zoom-in-95 duration-500">
                    <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-green-400" />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">Contrato Assinado!</h1>
                    <p className="text-white/60 mb-8">
                        Parabéns! O contrato foi formalizado com sucesso. Você receberá uma cópia e os dados de acesso no e-mail cadastrado.
                    </p>

                    <div className="space-y-4">
                        <div className="bg-white/5 p-4 rounded-xl text-left border border-white/5">
                            <p className="text-xs text-white/40 uppercase tracking-wider font-semibold mb-1">Próximos Passos</p>
                            <ul className="text-sm text-white/80 space-y-2">
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-500/50" />
                                    Verifique seu e-mail para credenciais de acesso.
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-500/50" />
                                    Aguarde a ativação do seu ambiente (até 24h).
                                </li>
                            </ul>
                        </div>

                        <button
                            onClick={() => window.location.href = 'https://crm.quadradois.com.br/login'}
                            className="w-full py-3 bg-white text-slate-900 font-bold rounded-xl hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
                        >
                            Ir para o Login <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    // ==================== Render Main Contract View ====================
    const isPF = contract.tenant_type === 'PF'
    const docLabel = isPF ? 'Confirme seu CPF' : 'Confirme o CNPJ da Empresa'

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 font-sans text-slate-100">
            {/* Header */}
            <header className="bg-white/5 border-b border-white/10 px-4 py-4 sticky top-0 z-50 backdrop-blur-md">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-tr from-sky-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold">
                            Q2
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-white leading-tight">Contrato SaaS</h1>
                            <p className="text-xs text-white/50">Quadra Dois Tecnologia</p>
                        </div>
                    </div>

                    <div className="hidden md:flex items-center gap-4 text-sm text-white/50">
                        <span className="flex items-center gap-1">
                            <HelpCircle className="w-4 h-4" /> Suporte
                        </span>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 py-8">
                {/* Timeline */}
                <div className="mb-10 max-w-3xl mx-auto">
                    <ProgressTimeline currentStepId={currentStep} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* LEFTSIDE: Context & Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Trust Badges */}
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                            <TrustBadges />
                        </div>

                        {/* Context Card (Plan Details) */}
                        <SaaSContextCard
                            plan={{
                                name: contract.plan_name || 'Plano Enterprise',
                                price: parsePrice(contract.monthly_price),
                            }}
                            duration={{
                                months: contract.duration_months || 12,
                                startDate: new Date().toISOString(),
                                renewalType: 'automatic'
                            }}
                            companyName="Quadra Dois Tecnologia Ltda"
                        />

                        {/* Contract Text */}
                        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                            <button
                                onClick={() => setShowFullContract(!showFullContract)}
                                className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
                            >
                                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-amber-400" />
                                    Termos do Contrato
                                </h3>
                                {showFullContract ? <ChevronUp className="text-white/50" /> : <ChevronDown className="text-white/50" />}
                            </button>

                            {showFullContract && (
                                <div className="px-6 pb-6 text-sm text-white/70 space-y-4 max-h-[500px] overflow-y-auto custom-scrollbar">
                                    <p><strong>CLÁUSULA 1 - DO OBJETO:</strong> O presente instrumento tem como objeto o licenciamento de uso do software (SaaS) Quadra Dois CRM.</p>
                                    <p><strong>CLÁUSULA 2 - DA VIGÊNCIA:</strong> O contrato vigorará pelo prazo de 12 (doze) meses, renovando-se automaticamente.</p>
                                    <p><strong>CLÁUSULA 3 - DO PAGAMENTO:</strong> A CONTRATANTE pagará à CONTRATADA o valor mensal descrito no resumo do plano.</p>
                                    <p><strong>CLÁUSULA 4 - DA CONFIDENCIALIDADE:</strong> As partes comprometem-se a manter sigilo sobre as informações técnicas e comerciais.</p>
                                    <p><strong>CLÁUSULA 5 - DA PROTEÇÃO DE DADOS:</strong> A CONTRATADA atuará em conformidade com a LGPD, garantindo a segurança dos dados inseridos.</p>
                                    <div className="p-4 bg-white/5 rounded-lg text-xs italic">
                                        * Este é um resumo. O contrato completo possui validade jurídica plena conforme legislação vigente.
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Consents */}
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                            <ConsentCheckboxes onConsentChange={(all, consents) => handleConsentChange(all, consents)} />
                        </div>
                    </div>

                    {/* RIGHTSIDE: Action (Sticky) */}
                    <div className="space-y-6">
                        {/* Parties Card */}
                        <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-4">
                            <div>
                                <div className="flex items-center gap-2 mb-2 text-sky-400 font-medium text-sm uppercase tracking-wider">
                                    <Building2 className="w-4 h-4" /> Contratante
                                </div>
                                <p className="text-white font-semibold text-lg">{contract.client_name}</p>
                                <p className="text-white/50 text-sm">
                                    {isPF ? 'CPF' : 'CNPJ'}: {contract.client_document_masked || '---'}
                                </p>
                            </div>
                            <div className="border-t border-white/10 pt-4">
                                <div className="flex items-center gap-2 mb-2 text-purple-400 font-medium text-sm uppercase tracking-wider">
                                    <Building2 className="w-4 h-4" /> Contratada
                                </div>
                                <p className="text-white font-semibold">Quadra Dois Tecnologia</p>
                                <p className="text-white/50 text-sm">CNPJ: 54.912.187/0001-99</p>
                            </div>
                        </div>

                        {/* Signature Card */}
                        <div className="lg:sticky lg:top-24 bg-gradient-to-b from-sky-500/10 to-transparent border border-sky-500/20 rounded-2xl p-6 shadow-2xl shadow-sky-900/10 backdrop-blur-sm">
                            <div className="mb-6">
                                <label className="block text-sm text-white/70 mb-2 font-medium">
                                    {docLabel}
                                </label>
                                <input
                                    type="text"
                                    value={documentInput}
                                    onChange={(e) => setDocumentInput(e.target.value)}
                                    placeholder={isPF ? "000.000.000-00" : "00.000.000/0000-00"}
                                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-all font-mono"
                                />
                            </div>

                            <SignatureField
                                onSignatureChange={handleSignatureChange}
                                strokeColor="#0ea5e9" // sky-500
                                className="mb-6"
                            />

                            <button
                                onClick={handleSign}
                                disabled={signing || !allConsentsGiven || !signatureValid}
                                className="w-full py-4 bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-400 hover:to-indigo-500 disabled:from-slate-700 disabled:to-slate-800 disabled:cursor-not-allowed text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-sky-500/25 active:scale-[0.98]"
                            >
                                {signing ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" /> Processando...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-5 h-5" /> Confirmar Assinatura
                                    </>
                                )}
                            </button>

                            {!allConsentsGiven && (
                                <p className="text-xs text-center text-white/40 mt-3">
                                    Preencha os dados e aceite os termos para liberar.
                                </p>
                            )}
                        </div>
                    </div>
                </div>

            </main>
        </div>
    )
}

export default SaaSContractSignPage
